// src/components/DokumentyForm.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useFormData } from "../context/FormDataContext";
import OcrOhledaci from "./OcrOhledaci";
import {
  apiFetch as authApiFetch,
  logout as authLogout,
  API_BASE,
  getToken,
} from "../auth/authClient";

const cardStyle = { border: "1px solid #333", borderRadius: 12, padding: 12, marginBottom: 12 };
const btnStyle = { padding: "10px 14px", borderRadius: 10, border: "1px solid #444", cursor: "pointer", fontWeight: 900 };
const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #444", outline: "none" };
const labelStyle = { fontSize: 13, fontWeight: 800, marginBottom: 6 };

function isHtmlLike(s) {
  const t = String(s || "").trim().toLowerCase();
  return t.startsWith("<!doctype html") || t.startsWith("<html") || t.includes("<pre>");
}

function prettyServerError(errJson, status, rawText) {
  if (isHtmlLike(rawText)) {
    const m = String(rawText || "").match(/Cannot (GET|POST|PUT|DELETE)\s+([^\s<]+)/i);
    const hint = m ? `Cannot ${m[1].toUpperCase()} ${m[2]}` : "Server vrátil HTML error";
    return `Chyba ${status}: ${hint}\n\nTip: zkontroluj, že backend běží a má route /api/generuj-dokumenty.`;
  }

  if (!errJson) return `Chyba ${status}: ${rawText || "bez detailu"}`;

  const parts = [
    `Chyba ${status}`,
    errJson.fileName ? `Soubor: ${errJson.fileName}` : null,
    errJson.file ? `Soubor: ${errJson.file}` : null,
    errJson.templatePath ? `Šablona: ${errJson.templatePath}` : null,
    errJson.folder ? `Složka: ${errJson.folder}` : null,
    errJson.detail ? `Detail: ${errJson.detail}` : null,
    errJson.error ? `Error: ${errJson.error}` : null,
    errJson.missing ? `Missing: ${JSON.stringify(errJson.missing)}` : null,
    errJson.availablePdf ? `PDF šablony: ${JSON.stringify(errJson.availablePdf)}` : null,
  ].filter(Boolean);

  return parts.join("\n");
}

async function safeReadText(res) {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

function tryParseJson(text) {
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return null;
  }
}

function getFilenameFromDisposition(disposition) {
  if (!disposition) return "";
  const m1 = disposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (m1 && m1[1]) {
    try {
      return decodeURIComponent(m1[1]);
    } catch {
      return m1[1];
    }
  }
  const m2 = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (m2 && m2[1]) return m2[1];
  return "";
}

function labelForPdf(file) {
  const low = String(file || "").toLowerCase();

  if (low.includes("rakvenka")) return "Rakvenka";
  if (low.includes("rec_pro_recnika")) return "Řeč pro řečníka";
  if (low.includes("vyzvednuti_urny")) return "Plná moc – vyzvednutí urny (CB)";
  if (low.includes("souhlas")) return "Plná moc – souhlas";
  if (low.includes("protokol_o_predani")) return "Protokol o předání zesnulého (CB)";

  return String(file || "")
    .replace(/\.pdf$/i, "")
    .replace(/[_-]+/g, " ")
    .trim();
}

function safePart(v) {
  return String(v || "")
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_");
}

function fmtDateForName(d) {
  const s = String(d || "").trim();
  if (!s) return "";
  if (/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(s)) {
    const [dd, mm, yyyy] = s.split(".");
    const pad2 = (x) => String(x).padStart(2, "0");
    return `${yyyy}-${pad2(mm)}-${pad2(dd)}`;
  }
  if (s.includes("T")) return s.split("T")[0];
  return s;
}

// ✅ JSON helper přes authClient (token + baseURL + jednotná chyba)
async function apiJson(path, opts = {}) {
  try {
    const headersIn = (opts && typeof opts === "object" ? opts.headers : null) || {};
    const headers = { "Content-Type": "application/json", ...headersIn };
    return await authApiFetch(path, { ...opts, headers });
  } catch (e) {
    if (e?.status === 401) authLogout();
    throw e;
  }
}

export default function DokumentyForm() {
  const { formData, mergeFormData } = useFormData();

  // 🌸 Květiny posílej do dokumentů jen když je zaškrtnuto v záložce Květiny
  const formDataForDocs = useMemo(() => {
    const include = Boolean(formData?.kvetiny?.includeDocs);
    const k = include ? formData?.kvetiny : undefined;
    return { ...formData, kvetiny: k };
  }, [formData]);

  // ✅ zápis do formData (jen přes mergeFormData)
  const setPath = useCallback(
    (pathStr, value) => {
      const parts = String(pathStr || "").split(".").filter(Boolean);
      if (!parts.length) return;

      let obj = {};
      let cur = obj;
      for (let i = 0; i < parts.length; i++) {
        const k = parts[i];
        if (i === parts.length - 1) cur[k] = value;
        else {
          cur[k] = {};
          cur = cur[k];
        }
      }
      if (typeof mergeFormData === "function") mergeFormData(obj);
    },
    [mergeFormData]
  );

  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [message, setMessage] = useState("");

  // ===== určení varianty kremace/pohřeb =====
  const kp = formData?.kremacePohreb || {};
  const typLower = String(kp?.typ || "kremace").toLowerCase();
  const isPohreb = typLower.includes("poh");
  const slozkaSablon = isPohreb ? "pohreb" : "kremace";

  // ===== data zesnulého (pro názvy) =====
  const zes = formData?.zesnulaOsoba || {};
  const vst = formData?.vstupniInformace || {};

  const zesJmeno = useMemo(
    () => String(zes?.jmeno || vst?.jmenoZesnuleho || "").trim(),
    [zes?.jmeno, vst?.jmenoZesnuleho]
  );
  const zesPrijmeni = useMemo(
    () => String(zes?.prijmeni || vst?.prijmeniZesnuleho || "").trim(),
    [zes?.prijmeni, vst?.prijmeniZesnuleho]
  );

  const zesDatumNar = useMemo(() => {
    return fmtDateForName(
      zes?.datumNarozeni || zes?.datum_narozeni || vst?.datumNarozeniZesnuleho || vst?.datumNarozeni || ""
    );
  }, [zes, vst]);

  const zesnulyJmenoFallback = useMemo(() => {
    const a = [zesJmeno, zesPrijmeni].filter(Boolean).join(" ").trim();
    return a || "objednavka";
  }, [zesJmeno, zesPrijmeni]);

  const baseName = useMemo(() => {
    const p = safePart(zesPrijmeni);
    const j = safePart(zesJmeno);
    const d = safePart(zesDatumNar);
    const out = [p, j, d].filter(Boolean).join("_");
    return out || safePart(zesnulyJmenoFallback) || "objednavka";
  }, [zesPrijmeni, zesJmeno, zesDatumNar, zesnulyJmenoFallback]);

  // ===== Protokol údaje (jen kremace) =====
  const predaniCB = formData?.predaniCB || {};
  const datumPredani = predaniCB?.datumPredani || "";
  const hodinaPredani = predaniCB?.hodinaPredani || "";
  const poznamkaPredani = predaniCB?.poznamka || "";

  const typRakve = String(formData?.rakve?.typRakve || formData?.typRakve || "").trim();

  // ===== PDF seznam z backendu =====
  const [availablePdf, setAvailablePdf] = useState([]);
  const [selected, setSelected] = useState([]);

  const pingServer = useCallback(async () => {
    await apiJson("/api/ping", { method: "GET" });
    return true;
  }, []);

  const loadAvailablePdf = useCallback(async () => {
    setIsLoadingPdf(true);
    setMessage("");
    try {
      await pingServer();
      // ✅ načti seznam PDF bez apiJson (eliminuje baseURL/caching/auth wrapper problémy)
      const token = getToken();
      const resp = await fetch(`${API_BASE}/api/sablony/pdf?folder=${encodeURIComponent(slozkaSablon)}`, {
        method: "GET",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        cache: "no-store",
      });
      if (!resp.ok) {
        const raw = await safeReadText(resp);
        throw new Error(`HTTP ${resp.status}: ${raw.slice(0, 300)}`);
      }
      const json = await resp.json();

      // 🔒 skryté šablony (nechceme v UI)
      const HIDE = new Set([
        "Hlaseni_umrti_pro_matriku_univerzalni_V3.pdf",
        "Rakvenka000.pdf",
      ]);

      const pdfRaw = Array.isArray(json?.pdf) ? json.pdf : [];
      const pdfBase = pdfRaw.filter((f) => !HIDE.has(String(f || "")));
      const extraPdf = ["Rec_pro_recnika.pdf"];
      const pdf = Array.from(new Set([...pdfBase, ...extraPdf]));

      setAvailablePdf(pdf);

      if (pdf.length === 0) {
        setSelected([]);
      } else if (isPohreb) {
        setSelected([]);
      } else {
        setSelected(pdf.slice());
      }
    } catch (e) {
      setAvailablePdf([]);
      setSelected([]);
      setMessage(`❌ Chyba načtení PDF šablon: ${String(e?.message || e || "")}`);
    } finally {
      setIsLoadingPdf(false);
    }
  }, [pingServer, slozkaSablon, isPohreb]);

  useEffect(() => {
    loadAvailablePdf();
  }, [loadAvailablePdf]);

  const toggleDoc = useCallback((file) => {
    setSelected((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(file) ? arr.filter((x) => x !== file) : [...arr, file];
    });
  }, []);

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      setMessage("");

      if (!Array.isArray(selected) || selected.length === 0) {
        setMessage("❌ Nejdřív vyber aspoň jeden PDF dokument.");
        return;
      }

      await pingServer();

      const payload = {
        slozkaSablon,
        vybraneDokumenty: selected,
        formData: { ...(formDataForDocs || {}), ...(formData?.kvetiny?.includeDocs ? { kvetiny: formData.kvetiny } : {}) },
      };

      // ✅ BLOB download – ruční fetch s tokenem (přes authClient getToken)
      const token = getToken();

      const response = await fetch(`${API_BASE}/api/generuj-dokumenty`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        cache: "no-store",
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const rawText = await safeReadText(response);
        const errJson = tryParseJson(rawText);

        if (response.status === 401) authLogout();

        setMessage(`❌ ${prettyServerError(errJson, response.status, rawText)}`);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const cd = response.headers.get("Content-Disposition") || "";
      const serverName = getFilenameFromDisposition(cd);

      const fallbackName =
        selected.length === 1 ? `${baseName}_${slozkaSablon}_${selected[0]}` : `${baseName}_${slozkaSablon}_pdf.zip`;

      const a = document.createElement("a");
      a.href = url;
      a.download = serverName || fallbackName;

      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setMessage(selected.length === 1 ? `✅ Vygenerováno: ${selected[0]}` : `✅ Vygenerováno: ${selected.length} PDF (ZIP)`);
    } catch (error) {
      setMessage(`❌ Chyba: ${String(error?.message || error || "")}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const docs = useMemo(() => {
    return (availablePdf || []).map((file) => ({
      id: file,
      file,
      label: labelForPdf(file),
    }));
  }, [availablePdf]);

  return (
    <div style={{ padding: 16, maxWidth: 900 }}>
      <h2 style={{ fontSize: 22, fontWeight: 900, marginBottom: 8 }}>Dokumenty (PDF)</h2>

      <div
        style={{
          marginBottom: 12,
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #ccc",
          background: "#f5f5f5",
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        Varianta: <b>{isPohreb ? "POHŘEB" : "KREMACE"}</b> • Složka šablon: <b>{slozkaSablon}</b> • Složka/název:{" "}
        <b>{baseName}</b> • Server: <b>{API_BASE}</b>
      </div>

      {/* ✅ OCR BLOK */}
      <div style={cardStyle}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>📸 Ohledací list (OCR)</div>

        <OcrOhledaci
          onExtracted={(ex) => {
            const z = ex?.zesnuly ? ex.zesnuly : (ex || {});
            setPath("zesnulaOsoba.jmeno", z?.jmeno || "");
            setPath("zesnulaOsoba.prijmeni", z?.prijmeni || "");
            setPath("vstupniInformace.datumUmrti", z?.datumUmrti || "");
            setPath("vstupniInformace.mistoUmrti", z?.mistoUmrti || z?.adresaUmrti || "");
            alert("OCR hotovo – propsáno do formulářů.");
          }}
        />
      </div>

      {!isPohreb ? (
        <div style={cardStyle}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>🧾 Protokol (volitelné) – doplň údaje</div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div style={labelStyle}>Datum předání</div>
              <input
                type="date"
                value={datumPredani}
                onChange={(e) => setPath("predaniCB.datumPredani", e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={labelStyle}>Hodina předání</div>
              <input
                type="time"
                value={hodinaPredani}
                onChange={(e) => setPath("predaniCB.hodinaPredani", e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={labelStyle}>Poznámka</div>
            <input
              value={poznamkaPredani}
              onChange={(e) => setPath("predaniCB.poznamka", e.target.value)}
              placeholder="např. závady, nesrovnalosti…"
              style={inputStyle}
            />
          </div>

          <div style={{ marginTop: 10 }}>
            <div style={labelStyle}>Použitá rakev</div>
            <input
              value={typRakve}
              readOnly
              placeholder="Vyplní se automaticky z výběru rakve…"
              style={{ ...inputStyle, background: "#eee" }}
            />
          </div>
        </div>
      ) : null}

      <div style={cardStyle}>
        <div
          style={{
            fontWeight: 900,
            marginBottom: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span>📄 Výběr PDF dokumentů</span>
          <button
            onClick={loadAvailablePdf}
            disabled={isLoadingPdf}
            style={{ ...btnStyle, padding: "8px 12px", opacity: isLoadingPdf ? 0.7 : 1 }}
            title="Znovu načíst PDF ze serveru"
          >
            {isLoadingPdf ? "Načítám…" : "Obnovit seznam"}
          </button>
        </div>

        {isLoadingPdf ? (
          <div style={{ fontSize: 13, opacity: 0.85 }}>Načítám PDF šablony ze serveru…</div>
        ) : docs.length === 0 ? (
          <div style={{ fontSize: 13, opacity: 0.85 }}>
            Ve složce <b>{slozkaSablon}</b> nejsou žádné PDF šablony. <br />
            (Server: <b>/api/sablony/pdf?folder={slozkaSablon}</b>)
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {docs.map((d) => (
              <label
                key={d.id}
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  cursor: "pointer",
                  padding: "10px 12px",
                  borderRadius: 10,
                  border: "1px solid #444",
                }}
              >
                <input
                  type="checkbox"
                  checked={(Array.isArray(selected) ? selected : []).includes(d.file)}
                  onChange={() => toggleDoc(d.file)}
                  style={{ marginTop: 2 }}
                />
                <div>
                  <div style={{ fontWeight: 900 }}>{d.label}</div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    Soubor: <b>{d.file}</b>
                  </div>
                </div>
              </label>
            ))}
          </div>
        )}

        <button
          onClick={handleGenerate}
          disabled={isGenerating || docs.length === 0}
          style={{ ...btnStyle, marginTop: 12, opacity: isGenerating || docs.length === 0 ? 0.7 : 1 }}
        >
          {isGenerating ? "Generuji…" : selected.length <= 1 ? "Vygenerovat PDF" : "Vygenerovat ZIP"}
        </button>
      </div>

      {message ? (
        <pre style={{ marginTop: 12, fontWeight: 700, whiteSpace: "pre-wrap", background: "#111", color: "#fff", padding: 12, borderRadius: 10 }}>
          {message}
        </pre>
      ) : null}
    </div>
  );
}
